import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React from 'react';

import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import SaveLocationPicker, {
  DEFAULT_WORKLIST_PARAMS,
  LocationInformation,
  WorklistParams,
} from 'molbio/components/SaveLocationPicker';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {WorklistFormType} from 'worklists/WorklistConstants';

interface PickLocationStepRequiredParams {
  isRequired: true;
}

interface PickLocationStepNotRequiredParams {
  isRequired: false;
  checkboxLabel: string;
  checkboxTooltip: string;
  saveEntities: boolean;
  onUpdateSaveEntities: (saveEntities: boolean) => void;
  bannerText: string;
}

interface PickLocationStepProps {
  locationInfo: LocationInformation;
  onUpdateLocationInfo: (locationInfo: LocationInformation) => void;
  worklistParams: WorklistParams;
  stepDescription?: string;
  isRequiredParams: PickLocationStepRequiredParams | PickLocationStepNotRequiredParams;
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType> | null;
}

export function PickLocationStep(props: PickLocationStepProps): JSX.Element {
  const {isRequiredParams} = props;
  const saveEntities = isRequiredParams.isRequired || isRequiredParams.saveEntities;

  return (
    <>
      {!isRequiredParams.isRequired && (
        <>
          <div className={css(styles.bannerWrapper)}>
            <InlineBanner bannerType={InlineBannerType.INFO} text={isRequiredParams.bannerText} />
          </div>
          <div className={css(styles.checkboxWrapper)}>
            <CheckboxInput
              checked={saveEntities}
              id="save-entities-checkbox"
              label={isRequiredParams.checkboxLabel ?? ''}
              onChange={(e) => {
                isRequiredParams.onUpdateSaveEntities(e.target.checked);
              }}
            />
            <div className={css(styles.checkboxTooltip)}>
              <Tooltip text={isRequiredParams.checkboxTooltip}>
                <BenchlingIcon icon="info-circle" className={css(styles.checkboxIcon)} />
              </Tooltip>
            </div>
          </div>
        </>
      )}
      {saveEntities && (
        <>
          {props.stepDescription && <p className={css(styles.description)}>{props.stepDescription}</p>}
          <SaveLocationPicker
            targetFolderId={props.locationInfo.targetFolderId}
            onTargetFolderIdChange={(e) => {
              props.onUpdateLocationInfo({...props.locationInfo, targetFolderId: e.target.value});
            }}
            tagSchemaInfo={
              props.oligoAwareFolderItemTypes != null
                ? {
                    tagSchemaId: props.locationInfo.tagSchemaId,
                    onUpdateTagSchemaId: (tagSchemaId) => {
                      props.onUpdateLocationInfo({...props.locationInfo, tagSchemaId});
                    },
                    oligoAwareFolderItemTypes: props.oligoAwareFolderItemTypes,
                  }
                : null
            }
            worklistFormType={props.locationInfo.worklistParams?.worklistFormType ?? WorklistFormType.NEW}
            onWorklistFormTypeChange={(newOrExisting) => {
              if (newOrExisting === WorklistFormType.NEW) {
                props.onUpdateLocationInfo(
                  produce(props.locationInfo, (draft) => {
                    draft.worklistParams!.targetWorklistId = null;
                    draft.worklistParams!.worklistFormType = WorklistFormType.NEW;
                  })
                );
              } else {
                props.onUpdateLocationInfo(
                  produce(props.locationInfo, (draft) => {
                    draft.worklistParams!.newWorklistInfo = null;
                    draft.worklistParams!.worklistFormType = WorklistFormType.EXISTING;
                  })
                );
              }
            }}
            newWorklistName={props.locationInfo.worklistParams?.newWorklistInfo?.name ?? ''}
            onNewWorklistNameChange={(e) => {
              props.onUpdateLocationInfo(
                produce(props.locationInfo, (draft) => {
                  if (e.target.value) {
                    draft.worklistParams!.newWorklistInfo = {name: e.target.value};
                  } else {
                    draft.worklistParams!.newWorklistInfo = null;
                  }
                })
              );
            }}
            onWorklistIdChange={(targetWorklist) => {
              props.onUpdateLocationInfo(
                produce(props.locationInfo, (draft) => {
                  draft.worklistParams!.targetWorklistId = targetWorklist == null ? null : targetWorklist.id;
                })
              );
            }}
            worklistParams={props.worklistParams}
            required={true}
            useWorklist={props.locationInfo.worklistParams != null}
            uniqueId="redesign"
            onUseWorklistChange={(useWorklist: boolean) => {
              props.onUpdateLocationInfo(
                produce(props.locationInfo, (draft) => {
                  draft.worklistParams = useWorklist ? DEFAULT_WORKLIST_PARAMS : null;
                })
              );
            }}
          />
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  bannerWrapper: {marginTop: 16},
  chevron: {paddingRight: 4},
  checkboxWrapper: {marginTop: 16, marginBottom: 16, display: 'flex'},
  checkboxIcon: {color: GRAY_DARKER, width: '16px', height: '16px'},
  checkboxTooltip: {marginLeft: 8},
  description: {marginTop: 10, marginBottom: 10},
});
